import { Component, h } from 'preact';

import { propertyTypes } from '../../../../../constants/properties';
import { InquiryPipelineFiltersUiModel } from 'inquiries/inquiry-pipeline-filters.model';
import { PropertyDivision, DivCat } from 'div-cat.model';

import { ChoicesButtonGroup } from '../../common/choices-button-group.component';
import { LinearLocationSearch } from '../../common/linear-location-search.component';
import { DatePickerControl } from '../../common/date-picker-control.component';
import { InquiryStageDefinition } from '../../../../../types/inquiries';

interface InquiryGridFiltersProps {
  onApply?: (filters: InquiryPipelineFiltersUiModel) => void;
  onChange?: (filters: InquiryPipelineFiltersUiModel) => void;
  onClose?: () => void;
  filters?: InquiryPipelineFiltersUiModel;

  currentStage: InquiryStageDefinition;
}
interface InquiryGridFiltersState {}

/**
 * THIS COMPONENT IS AND SHOULD ALWAYS BE STATELESS!
 * Storing state in this component requires *way* too much syncing effort with
 * InquiryGridPage.
 */
export class InquiryGridFilters extends Component<InquiryGridFiltersProps, InquiryGridFiltersState> {
  divisions = [ 'Residential', 'Commercial' ];
  vlChoices = [
    { label: 'All', value: null },
    { label: 'With VLs only', value: 1 },
    { label: 'Without VLs only', value: 0 }
  ];
  collectionChoices = [
    { label: 'All', value: null },
    { label: 'For collection only', value: 0 },
    { label: 'Collected only', value: 1 }
  ];

  getViewingListChoices(props: InquiryGridFiltersProps, state: InquiryGridFiltersState) {
    const filters = props.filters || {};
    const curValue = filters.viewingList === undefined ? null : filters.viewingList;

    return this.vlChoices.map(choice => (
      <div className="form-check my-2">
        <label className="mb-0">
          <input type="radio" className="form-check-input" value={ choice.value }
            onChange={ () => this.onInput({ viewingList: choice.value }) }
            checked={ curValue === choice.value } />
          { choice.label }
        </label>
      </div>
    ));
  }

  getTypeRadios(props: InquiryGridFiltersProps, state: InquiryGridFiltersState) {
    const filters = props.filters || {};
    return propertyTypes.filter(
      // Show all if no division filter is set, otherwise check with filter
      propType => !filters.division || propType.division === filters.division
    ).map(type => (
      <div className="form-check my-2">
        <label className="mb-0">
          <input type="radio" className="form-check-input" value={ type.label }
            onChange={ () => this.onInput({ type: type.label }) }
            checked={ filters.type === type.label } />
          { type.label }
        </label>
      </div>
    ));
  }

  getCategorySelection(props: InquiryGridFiltersProps, state: InquiryGridFiltersState) {
    const filters = props.filters || {};
    let selectedCategoryChoice: string;
    if (filters.category === 'Rent') {
      selectedCategoryChoice = 'For rent';
    } else if (filters.category === 'Sale') {
      selectedCategoryChoice = 'For sale';
    } else {
      selectedCategoryChoice = null;
    }

    return (
      <ChoicesButtonGroup choices={ ['For rent', 'For sale'] }
        selectedChoice={ selectedCategoryChoice }
        onSelect={ cat => this.onCategorySelect(cat)} />
    )
  }

  getCollectionFilterGroup(props: InquiryGridFiltersProps, state: InquiryGridFiltersState) {
    const filters = props.filters || {};
    const curValue = filters.collected === undefined ? null : filters.collected;

    // console.log('getCollectionFilterGroup: currentStage =', props.currentStage);
    if (props.currentStage && props.currentStage.slug === 'for-collection') {
      return (
        <div className="hi-inqfilters__group">
          <span className="hi-input-label mb-1">Collection Status</span>
        
          { this.collectionChoices.map(choice => (
            <div className="form-check my-2">
              <label className="mb-0">
                <input type="radio" className="form-check-input" value={ choice.value }
                  onChange={ () => this.onInput({ collected: choice.value }) }
                  checked={ curValue === choice.value } />
                { choice.label }
              </label>
            </div>
          )) }
        </div>
      );
    }
  }

  onCategorySelect(categoryLabel: string) {
    if (categoryLabel) {
      this.onInput({ category: categoryLabel === 'For rent' ? 'Rent' : 'Sale' });
    } else {
      this.onInput({ category: null });
    }
  }

  onMaxBudgetChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value);

    this.onInput({ maxBudgetTo: value });
  }

  onInput(changedFilters: InquiryPipelineFiltersUiModel) {
    const curFilters = this.props.filters || {};
    const newFilter = { ...curFilters, ...changedFilters };

    // console.log('Input:', changedFilters);

    if (curFilters.division !== newFilter.division) {
      newFilter.type = null;
    }

    if (newFilter.location && !curFilters.location) {
      newFilter.locationSearchInput = newFilter.location.label;
    } else if (newFilter.locationSearchInput != curFilters.locationSearchInput) {
      // User typed -- clear the location
      // Notice that we're in an 'else if' -- this cannot run if we just got a
      // location update
      newFilter.location = null;
    }
    
    // console.log('Sending new filters:', newFilter);
    if (this.props.onChange) {
      this.props.onChange(newFilter);
    }
  }

  onApply() {
    if (this.props.onApply) {
      this.props.onApply(this.props.filters);
    }
  }
  
  onCloseButtonClick(event: Event) {
    event.preventDefault();
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  getDivCat(filters: InquiryPipelineFiltersUiModel): DivCat {
    const { division, category, type } = filters || {};
    return { division, category, type };
  }

  render(props: InquiryGridFiltersProps, state: InquiryGridFiltersState) {
    const filters = props.filters || {};

    return (
      <div className="hi-inqfilters">
        <div className="hi-inqfilters__header">
          <span className="hi-inqfilters__heading">Set filter</span>
          <a href='#' className='hi-inqfilters__close-button' onClick={ event => this.onCloseButtonClick(event) }>
            <i className="fas fa-times" /> Close
          </a>
        </div>

        <div className="hi-inqfilters__body">
          <div className="form-group hi-inqfilters__group">
            <label className="hi-input-label mb-1">Move-in Date (From - To)</label>
            <div className="d-flex">
              <DatePickerControl className="form-control w-50 hi-border-radius-right-0"
                onChange={ evt => this.onInput({ dateMoveInFrom: evt.target['value'] }) }
                value={ filters.dateMoveInFrom } />
              <DatePickerControl className="form-control w-50 hi-border-radius-left-0"
                onChange={ evt => this.onInput({ dateMoveInTo: evt.target['value'] }) }
                value={ filters.dateMoveInTo } />
            </div>
          </div>

          <div className="form-group hi-inqfilters__group">
            <label className="hi-input-label mb-1">Location</label>
            <LinearLocationSearch 
              onInput={ locationSearchInput => this.onInput({ locationSearchInput }) }
              onLocationChange={ location => this.onInput({ location }) }
              searchString={ filters.locationSearchInput }
              location={ filters.location } />
          </div>


          <div className="form-group hi-inqfilters__group">
            <label className="hi-input-label mb-1">Inquiry Date (From - To)</label>
            <div className="d-flex">
              <DatePickerControl className="form-control w-50 hi-border-radius-right-0"
                onChange={ evt => this.onInput({ dateCreatedFrom: evt.target['value'] }) }
                value={ filters.dateCreatedFrom } />
              <DatePickerControl className="form-control w-50 hi-border-radius-left-0"
                onChange={ evt => this.onInput({ dateCreatedTo: evt.target['value'] }) }
                value={ filters.dateCreatedTo } />
            </div>
          </div>

          <div className="hi-inqfilters__group">
            <span className="hi-input-label mb-1">Viewing List Attachment</span>
            { this.getViewingListChoices(props, state) }
          </div>

          { this.getCollectionFilterGroup(props, state) }

          <div className="hi-inqfilters__group">
            <h3 className="hi-input-label mb-1">Property Kind</h3>
            <ChoicesButtonGroup choices={ this.divisions }
              selectedChoice={ filters.division } 
              onSelect={ (division: PropertyDivision) => this.onInput({ division }) }/>
          </div>
          

          <div className="hi-inqfilters__group">
            <h3 className="hi-input-label mb-1">Category</h3>
            { this.getCategorySelection(props, state) }
          </div>

          <div className="hi-inqfilters__group">
            <h3 className="hi-input-label mb-1">Property Type</h3>
            { this.getTypeRadios(props, state) }
          </div>

          <div className="form-group hi-inqfilters__group">
            <label className="hi-input-label">Max Budget</label>
            <input type="number" placeholder="PHP" className="form-control"
              onChange={ event => this.onMaxBudgetChange(event) }
              value={ filters.maxBudgetTo } />
          </div>
        </div>
        
        <div className="hi-inqfilters__bottom-buttons">
          <button className="btn btn-secondary w-100" onClick={ () => this.onApply() }>
            Filter
          </button>
        </div>
      </div>
    );
  }
}